import { isPhoneNumberValid } from 'utils/phoneNumber';
import { RequiredErrorMessage, zNonEmptyString } from 'utils/validators/zodHelpers';
import { z } from 'zod';

export enum PCPInputMode {
  Manual = 'manual',
  Auto = 'auto',
  disabled = 'disabled',
}

export const PickNPIPhysicianSchema = z.object({
  pickedPhysicianNPICode: z.string(),
});

export const PCPSearchSchema = z.object({
  firstName: z.string().optional(),
  lastName: zNonEmptyString,
  city: z.string().optional(),
  stateCode: zNonEmptyString,
});

export const manualPCPSchema = z.object({
  fullName: zNonEmptyString,
  office: zNonEmptyString,
  phone: z
    .string(RequiredErrorMessage)
    .optional()
    .refine(
      d => {
        if (!d) return true;
        return isPhoneNumberValid(d);
      },
      { message: 'Invalid phone number' },
    ),
  website: z.string().optional(),
});

export const autoPCPSchema = z.object({
  npiNumber: zNonEmptyString,
  fullName: zNonEmptyString.optional(),
  phone: zNonEmptyString.optional(),
  office: zNonEmptyString.optional(),
  faxNumber: zNonEmptyString.optional(),
  taxonomyCode: zNonEmptyString.optional(),
  firstName: zNonEmptyString.optional(),
  lastName: zNonEmptyString.optional(),
  email: zNonEmptyString.optional(),
});

export const signupPCPSchema = z
  .discriminatedUnion('mode', [
    autoPCPSchema.extend({ mode: z.literal(PCPInputMode.Auto) }),
    manualPCPSchema.extend({ mode: z.literal(PCPInputMode.Manual) }),
    z.object({
      mode: z.literal(PCPInputMode.disabled),
    }),
  ])
  .and(z.object({ isPCPRequired: z.boolean() }))
  .transform(d => {
    if (!d.isPCPRequired) return { ...d, mode: PCPInputMode.disabled };

    if (d.mode === PCPInputMode.disabled) return d;

    if (d.mode === PCPInputMode.Manual) {
      return {
        mode: PCPInputMode.Manual,
        fullName: d.fullName,
        office: d.office,
        phone: d.phone,
        website: d.website,
        npiNumber: undefined,
      };
    }

    if (d.mode === PCPInputMode.Auto) {
      return {
        mode: PCPInputMode.Auto,
        npiNumber: d.npiNumber,
        fullName: d.fullName,
        phone: d.phone,
        office: d.office,
        faxNumber: d.faxNumber,
        taxonomyCode: d.taxonomyCode,
        firstName: d.firstName,
        lastName: d.lastName,
        email: d.email,
      };
    }
  });

export type PCPSearchFormType = z.infer<typeof PCPSearchSchema>;
export type PCPManualModeFormType = z.infer<typeof manualPCPSchema>;
export type SignupPCPFormType = z.infer<typeof signupPCPSchema>;
export type PCPPickNPIFormType = z.infer<typeof PickNPIPhysicianSchema>;
export type AutoPCPFormType = z.infer<typeof autoPCPSchema>;
